import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { CardBody, Col, Row, Card } from "reactstrap";
import userImage6 from "../../assets/images/avatar.png";
import AddToBookmarkCandidate from "../../helpers/Common/AddToBookmarkCandidate";
import { getJobIndustries } from "../../api/categories";
import {
  getJobLocationById,
  getSalaryAmount,
  getTimeAgo,
  getWorkExpText,
  imageUrl,
  isUserActive,
  toArray,
} from "../../helpers/common";
import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
const CandidateRow = ({ details }) => {
  const [industriesList, setIndustriesList] = useState([]);


  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await getJobIndustries();
        // Handle both direct array or response.data.data / response.data
        const data = response?.data?.data || response?.data || response || [];
        if (Array.isArray(data)) {
          setIndustriesList(data);
        }
      } catch (error) {
        console.error("Failed to fetch job industries:", error);
      }
    };
    fetchIndustries();
  }, []);

  // 🟢 FIXED: two different API shapes feed this same component —
  //
  // 1) "All Candidates" list — fields are FLAT on `details` directly:
  //      details.userId, details.name, details.profileImage,
  //      details.designation, details.city, details.state,
  //      details.currentSalaryPerMonth, details.totalWorkExperience,
  //      details.additional_fields  → JSON STRING that needs JSON.parse
  //
  // 2) "Bookmarked Candidates" list — everything is NESTED:
  //      details.candidate.id / name / photo / city / state
  //      details.candidateAdditionalFields → already an OBJECT (not a string),
  //      and it holds designation / currentSalaryPerMonth / totalWorkExperience /
  //      skills / location, which live directly on `details` in shape (1).
  //
  // `candidateSource` picks whichever "primary" object actually has the
  // id/name/photo, and `additionalFields` normalizes the extra-fields blob
  // regardless of whether it arrived as a JSON string or already-parsed object.
  const candidateSource = details?.candidate || details;

  const rawAdditionalFields =
    details?.candidateAdditionalFields ?? details?.additional_fields;
  const additionalFields =
    typeof rawAdditionalFields === "string"
      ? rawAdditionalFields
        ? JSON.parse(rawAdditionalFields)
        : {}
      : rawAdditionalFields || {};

  // 🟢 FIXED: was `candidateSource?.id ?? candidateSource?.userId` —
  // on the All Candidates API response, `id` is a different/unrelated
  // record id, while `userId` is the actual candidate ID the backend
  // bookmark API expects. Using `id` first sent the wrong value and
  // caused "Candidate not found." on bookmark clicks. userId now takes
  // priority; falls back to `id` only if userId isn't present at all
  // (e.g. some other shape that only ever provides `id`).
  const candidateId = candidateSource?.userId ?? candidateSource?.id;
  const candidateName = (candidateSource?.name || candidateSource?.username || "").trim();
  // 🟢 FIXED: backend sometimes sends `candidate.photo` as a MALFORMED
  // duplicated URL, e.g.
  //   "https://site.com/uploads/profile_image/https://site.com/uploads/profile_image/xxx.jpg"
  // That field isn't null/empty, so it was winning the old fallback chain
  // and pointing at a broken (404) image — the default icon showed because
  // the image failed to load, not because the URL was missing.
  //
  // `additionalFields.profilePicture` is consistently clean, so it's now
  // checked FIRST. `sanitizeImageUrl` is a safety net for `photo` in case
  // it's ever used as a last resort — it strips any accidental doubled prefix.
  const sanitizeImageUrl = (url) => {
    if (!url) return url;
    const lastHttpIndex = url.lastIndexOf("http");
    return lastHttpIndex > 0 ? url.substring(lastHttpIndex) : url;
  };

  const candidatePhoto = sanitizeImageUrl(
    candidateSource?.profileImage ||
    additionalFields?.profilePicture ||
    candidateSource?.photo,
  );

  // Bookmark shape puts designation/salary/experience inside additionalFields;
  // all-candidates shape puts them directly on `details` — check both.
  const candidateDesignation =
    additionalFields?.designation || candidateSource?.designation;
  const candidateSalary =
    candidateSource?.currentSalaryPerMonth || additionalFields?.currentSalaryPerMonth;
  const candidateExperience =
    candidateSource?.totalWorkExperience || additionalFields?.totalWorkExperience;
  const candidateJobType =
    additionalFields?.job_type || additionalFields?.jobType;
  const candidateLastActive = candidateSource?.lastActiveAtReadable;

  // City/state — bookmark shape's candidate.city is actually a location id
  // string sometimes, so fall back through additionalFields.location too.
  const candidateCity = candidateSource?.city || additionalFields?.location;
  const candidateState = candidateSource?.state || additionalFields?.state;

  const skills = additionalFields ? toArray(additionalFields?.skills, ",") : [];
  const candidateDetailUrl = `/candidate/details/${candidateId}`;
  const jobLocations = useSelector((state) => state.jobLocations.data) || [];

  // 🟢 MAP INDUSTRY ID TO NAME FROM API RESPONSE
  const rawIndustryId = candidateSource?.industry || additionalFields?.industry;
  const matchedIndustry = industriesList.find(
    (item) => String(item.id) === String(rawIndustryId)
  );
  const candidateIndustryName = matchedIndustry?.name || "";

  return (
    <div className="candidate-modern-card position-relative mb-4">
      <Link to={candidateDetailUrl} className="text-decoration-none">
        <Card
          className="border-0 shadow-sm custom-hover-card"
          style={{ borderRadius: "16px", overflow: "hidden" }}
        >
          <CardBody className="p-3 p-lg-4">
            <Row className="align-items-center">
              {/* Avatar Section */}
              <Col
                lg={2}
                md={3}
                className="text-center text-md-start mb-3 mb-md-0"
              >
                <div className="avatar-wrapper position-relative d-inline-block">
                  <img
                    src={candidatePhoto ? `${candidatePhoto}` : userImage6}
                    alt="Profile"
                    className="rounded-circle img-thumbnail shadow-sm"
                    style={{
                      width: "90px",
                      height: "90px",
                      objectFit: "cover",
                      border: "3px solid #fff",
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = userImage6;
                    }}
                  />
                  {/* Status Indicator Dot */}
                </div>
              </Col>

              {/* Main Content Section */}
              <Col lg={7} md={6}>
                <div className="candidate-info">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <h5 className="fs-18 fw-bold mb-0 text-dark text-capitalize">
                      {candidateName}
                    </h5>
                    <span
                      className="badge bg-light text-success fw-semibold rounded-pill px-2 py-1 border border-success-subtle"
                      style={{ fontSize: "11px" }}
                    >
                      <i className="uil uil-clock-three me-1"></i>
                      Active {candidateLastActive || "Recently"}
                    </span>
                  </div>

                  <h6
                    className="text-primary fw-semibold mb-2"
                    style={{ fontSize: "15px" }}
                  >
                    {candidateDesignation || "Designation Not Provided"}
                  </h6>

                  <div className="d-flex flex-wrap gap-3 text-muted small mb-3">
                    <span className="d-flex align-items-center">
                      <Icon
                        icon="solar:map-point-bold-duotone"
                        className="text-primary me-1"
                        width="16"
                      />
                      {/* {getJobLocationById(details?.location, jobLocations)} */}
                      {candidateCity && candidateState
                        ? `${candidateCity}, ${candidateState}`
                        : candidateCity
                          ? candidateCity
                          : candidateState
                            ? candidateState
                            : additionalFields?.location || "Location not specified"}
                    </span>
                    {candidateSalary && <span>₹ {candidateSalary}</span>}
                    {candidateJobType && (
                      <span className="d-flex align-items-center">
                        <Icon
                          icon="solar:clock-circle-bold-duotone"
                          className="text-warning me-1"
                          width="16"
                        />
                        {candidateJobType}
                      </span>
                    )}
                    {candidateExperience && (
                      <span className="d-flex align-items-center">
                        <Icon
                          icon="solar:case-bold-duotone"
                          width="16"
                          className="me-1 text-primary"
                        />
                        {candidateExperience}
                      </span>
                    )}
                    {candidateIndustryName && (
                      <span className="d-flex align-items-center">
                        <Icon
                          icon="solar:buildings-bold-duotone"
                          width="16"
                          className="me-1 text-info"
                        />
                        {candidateIndustryName}
                      </span>
                    )}
                  </div>

                  {/* Skills Section */}
                  {/* <div className="d-flex flex-wrap gap-1">
                    {skills?.slice(0, 5).map((skill, key) => (
                      <span key={key} className="skill-tag">
                        {skill}
                      </span>
                    ))}
                    {skills?.length > 5 && (
                      <span className="skill-tag">+{skills.length - 5}</span>
                    )}
                  </div> */}
                </div>
              </Col>

              {/* Action Section */}
              <Col lg={3} md={3} className="text-md-end mt-3 mt-md-0">
                <div className="d-grid d-md-block">
                  <button className="btn btn-outline-primary btn-sm rounded-pill px-3 fw-bold shadow-primary transition-all mt-md-5">
                    View Profile
                  </button>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Link>

      {/* Bookmark - Absolute Positioned to top-right of the whole area */}
      <div
        className="position-absolute"
        style={{ top: "20px", right: "20px", zIndex: 2 }}
      >
        {/* 🟢 Uses normalized candidateId — works for both flat (userId) and
            nested (candidate.id) API response shapes */}
        <AddToBookmarkCandidate candidateId={candidateId} />
      </div>
    </div>
  );
};

export default CandidateRow;
